/**
 * Adjustment Input Component
 *
 * 自然語言調整輸入框
 * - 提供範例引導（Phase 1: 僅當天內移動）
 * - 英文介面（符合 CLAUDE.md 規範）
 * - Loading 狀態
 */

import React, { useState } from 'react';
import { Send, Loader2, Edit3 } from '../common/icons';

interface AdjustmentInputProps {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const AdjustmentInput: React.FC<AdjustmentInputProps> = ({
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [inputText, setInputText] = useState('');

  // 範例文字（Phase 1: 僅支援當天內移動）
  const examples = [
    "Move morning meeting to 3pm",
    "Delay deep work by 1 hour",
    "Delay deep work by 30 minutes"
  ];

  const placeholderText = "Tell me what needs to change...";

  const handleSubmit = () => {
    if (inputText.trim() && !isLoading) {
      onSubmit(inputText.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-4">
      {/* 標題 */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <Edit3 size={18} className="text-orange-600" />
          Adjust Schedule
        </h3>
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      {/* 輸入框 */}
      <div className="relative">
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholderText}
          disabled={isLoading}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
        />

        {/* 提交按鈕 */}
        <button
          onClick={handleSubmit}
          disabled={!inputText.trim() || isLoading}
          className="absolute bottom-3 right-3 p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>

      {/* 範例引導 */}
      {!isLoading && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Examples:
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((example, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full cursor-default"
              >
                {example}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
